import React, { useState, useEffect } from 'react'
import { Row, Col, Alert } from 'antd'
import { SearchOutlined, InfoCircleFilled } from '@ant-design/icons'
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus'
import ActiveAssessmentFilter from './ActiveAssessmentFilter'
import CompletedFilter from './CompletedAssessmentFilter'
import AssessmentTable from './AssessmentTable'
import { generatePDFReport } from '../../actions'

import {
  AssessmentCard,
  AssessmentHeader,
  SearchSection,
  SearchInput,
  ShowingText,
  NoteSection,
  TabsContainer,
  Tab,

  ShowingTextContainer
} from '../../Style'

import { useDispatch } from 'react-redux'

const AssessmentContainer = ({
  setAssessment,
  activeAssessments,
  activeAssessmentsLoading,
  activeAssessmentsFilters,
  completedAssessments,
  completedAssessmentsLoading,
  completedAssessmentsFilters,
  fetchActiveAssessments,
  fetchCompletedAssessments,
  setActiveAssessmentFilters,
  setCompletedAssessmentFilters,
  setCurrentAssessmentId,
  studentId,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('active')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  // Contract state for the banner. Per-card locks ride on the assessment rows
  // themselves; this covers the case where the list is empty.
  const { subscription } = useSubscriptionStatus(studentId)

  const dispatch = useDispatch()

  useEffect(() => {
    if (studentId) {
      fetchActiveAssessments(studentId, {
        duration: activeAssessmentsFilters?.duration || [],
        assessmentType: activeAssessmentsFilters?.assessmentType || [],
        search: searchQuery,
      })
    }
  }, [activeAssessmentsFilters, searchQuery, fetchActiveAssessments, studentId])

  useEffect(() => {
    if (studentId) {
      fetchCompletedAssessments(studentId, {
        timeFilter: completedAssessmentsFilters?.timeFilter || [],
        assessmentType: completedAssessmentsFilters?.assessmentType || [],
        search: searchQuery,
      })
    }
  }, [
    completedAssessmentsFilters,
    searchQuery,
    fetchCompletedAssessments,
    studentId,
  ])



  const handleDeadlineChange = value => {
    setActiveAssessmentFilters({
      ...activeAssessmentsFilters,
      duration: value,
    })
  }

  const handleAssessmentTypeChange = checkedValues => {
    if (activeTab === 'active') {
      setActiveAssessmentFilters({
        ...activeAssessmentsFilters,
        assessmentType: checkedValues,
      })
    } else {
      setCompletedAssessmentFilters({
        ...completedAssessmentsFilters,
        assessmentType: checkedValues,
      })
    }
  }
  const handleTimeFilterChange = value => {
    setCompletedAssessmentFilters({
      ...completedAssessmentsFilters,
      timeFilter: value,
    })
  }

  const handleClearAll = () => {
    if (activeTab === 'active') {
      setActiveAssessmentFilters({
        ...activeAssessmentsFilters,
        duration: [''],
        assessmentType: [],
      })
    } else {
      setCompletedAssessmentFilters({
        ...completedAssessmentsFilters,
        timeFilter: [''],
        assessmentType: [],
      })
    }
  }

  const handleSearch = e => {
    const value = e.target.value
    setSearchKeyword(value)
    setSearchQuery(value)
  }

  const filterAssessments = (assessments, query) => {
    if (!query) return assessments
    return assessments?.filter(
      assessment =>
        assessment.title?.toLowerCase().includes(query.toLowerCase()) ||
        assessment.type?.toLowerCase().includes(query.toLowerCase()) ||
        assessment.name?.toLowerCase().includes(query.toLowerCase())
    )
  }

  const handleTabClick = tab => {
    setActiveTab(tab)
  }

  // const handleViewReport = async (assessmentId) => {
  //   try {
  //     setPdfLoadingId(assessmentId); // Set the ID of the report being generated
  //     // Your existing PDF generation code
  //     await generatePDF(assessmentId);
  //     // ...
  //   } catch (error) {
  //     console.error("Error generating PDF:", error);
  //   } finally {
  //     setPdfLoadingId(null); // Clear the loading ID when done
  //   }
  // };

  const handleViewReport = async (assessmentId) => {
    try {
      setPdfLoading(true);
      setPdfLoadingId(assessmentId); // Set the ID of the report being generated

      // Use the properly imported generatePDFReport function from actions
      await dispatch(generatePDFReport(assessmentId));
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setPdfLoading(false);
      setPdfLoadingId(null); // Clear the loading ID when done
    }
  };
  const handleTakeAssessment = assessment => {
    setCurrentAssessmentId(assessment)
    setAssessment(assessment)
  }
  const totalItems =
    activeTab === 'active'
      ? activeAssessments?.length || 0
      : completedAssessments?.length || 0

  return (
    <>
      <Row gutter={40}>
        {activeTab !== 'reports' && (
          <Col xs={24} sm={24} md={6} lg={6}>
            {activeTab === 'active' ? (
              <ActiveAssessmentFilter
                deadlineFilter={activeAssessmentsFilters?.duration || []}
                assessmentTypeFilter={
                  activeAssessmentsFilters?.assessmentType || []
                }
                onDeadlineChange={handleDeadlineChange}
                onAssessmentTypeChange={handleAssessmentTypeChange}
                onClearAll={handleClearAll}
              />
            ) : (
              <CompletedFilter
                timeFilter={completedAssessmentsFilters?.timeFilter || []}
                assessmentTypeFilter={
                  completedAssessmentsFilters?.assessmentType || []
                }
                onTimeFilterChange={handleTimeFilterChange}
                onAssessmentTypeChange={handleAssessmentTypeChange}
                onClearAll={handleClearAll}
              />
            )}
          </Col>
        )}
        <Col
          xs={24}
          sm={24}
          md={activeTab === 'reports' ? 24 : 18}
          lg={activeTab === 'reports' ? 24 : 18}
        >
          <AssessmentCard>
            <AssessmentHeader>Assessments</AssessmentHeader>
            {activeTab !== 'reports' && (
              <SearchSection>
                <SearchInput
                  placeholder="Search by keyword"
                  prefix={<SearchOutlined style={{ color: '#8C8C8C' }} />}
                  value={searchKeyword}
                  onChange={handleSearch}
                />
                <ShowingTextContainer>
                  <ShowingText>
                    Showing {totalItems} of {totalItems}
                  </ShowingText>
                </ShowingTextContainer>
              </SearchSection>
            )}
            {subscription.isLocked && (
              <Alert
                role="alert"
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  subscription.status === 'NOT_STARTED'
                    ? 'Subscription not active yet'
                    : 'Subscription expired'
                }
                description={subscription.message}
              />
            )}
            {activeTab === 'active' && (
              <NoteSection>
                <InfoCircleFilled />
                Note : For cognitive aptitude and technical assessments, please
                check your email for invites.
              </NoteSection>
            )}
            <TabsContainer>
              <Tab
                active={activeTab === 'active'}
                onClick={() => handleTabClick('active')}
              >
                Active Assessments
              </Tab>
              <Tab
                active={activeTab === 'completed'}
                onClick={() => handleTabClick('completed')}
              >
                Completed Assessments
              </Tab>
              <Tab
                active={activeTab === 'reports'}
                onClick={() => handleTabClick('reports')}
              >
                Reports
              </Tab>
            </TabsContainer>
            <AssessmentTable
              activeTab={activeTab}
              activeAssessments={filterAssessments(
                activeAssessments,
                searchQuery
              )}
              completedAssessments={filterAssessments(
                completedAssessments,
                searchQuery
              )}
              handleTakeAssessment={handleTakeAssessment}
              handleViewReport={handleViewReport}
              activeAssessmentsLoading={activeAssessmentsLoading}
              completedAssessmentsLoading={completedAssessmentsLoading}
              pdfLoading={pdfLoading}
              pdfLoadingId={pdfLoadingId}
            />
          </AssessmentCard>
        </Col>
      </Row>
    </>
  )
}



export default AssessmentContainer
